import { useCallback, useEffect } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { useOnScreen } from "@ascendnow/shared";

import { useResponsive } from "../../lib";
import AscButton from "../AscButton/AscButton";
import AscTag from "../AscTag/AscTag";
import { ClockIcon } from "../AscIcons";
import styles from "./AscNotificationItem.module.scss";
import { AscNotificationItemProps } from "./AscNotificationItem.types";
import AscImage from "../AscImage/AscImage";
import SampleImage from "../../assets/images/badge.png";

dayjs.extend(relativeTime);

export default function AscNotificationItem({
  id,
  title,
  imageUrl,
  date,
  type,
  read,
  action,
  onClick,
  onVisible,
  onKeyDown,
}: AscNotificationItemProps) {
  const { isVisible, ref } = useOnScreen<HTMLDivElement>();
  const { isLarge, isMedium } = useResponsive();

  const getNotificationDate = useCallback(
    () => (
      <div className={styles.dateWrapper}>
        <ClockIcon className={styles.dateIcon} />
        <div className={styles.date}>
          {dayjs().diff(dayjs(date), "day") === 0
            ? dayjs(date).toNow(true)
            : dayjs(date).format("h:mm A")}
        </div>
      </div>
    ),
    [date]
  );

  useEffect(() => {
    if (isVisible && onVisible && !read) {
      onVisible(id);
    }
  }, [isVisible, read, id]);

  return (
    <div
      className={classNames(styles.ground, {
        [styles.read]: read,
        [styles.large]: isLarge || type === "tag",
      })}
      onClick={onClick}
      ref={ref}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <div>{!isMedium && getNotificationDate()}</div>
      <div className={styles.content}>
        <div className={styles.contentLeft}>
          <div className={styles.imageWrapper}>
            <AscImage className={styles.image} src={imageUrl || SampleImage} />
          </div>
          <div className={styles.contentDetails}>
            <div
              className={styles.title}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <div className={styles.actions}>
              {type !== "tag" && !isMedium && (
                <AscTag
                  size="small"
                  type={type}
                  label={type}
                  className={styles.tag}
                />
              )}
              {action ? (
                <AscButton fitContent size="small" onClick={onClick}>
                  {action}
                </AscButton>
              ) : null}
            </div>
          </div>
        </div>
        <div className={styles.contentRight}>
          {isMedium && getNotificationDate()}
          {type !== "tag" && isMedium && (
            <AscTag size="small" type={type} label={type} />
          )}
        </div>
      </div>
    </div>
  );
}
